//explicitly splited this code here to avoid long page file length in SearchPlpPage.tsx

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '@/app/store/store';
import { selectFooterInViewport } from '@/app/store/footerVisibilitySlice';
import { usePlpListing } from '@/features/plp/presentation/hooks/usePlpListing';
import { usePlpRedirectUrlNavigation } from '@/features/plp/presentation/hooks/usePlpRedirectUrlNavigation';
import { usePlpSidQueryHandlers } from '@/features/plp/presentation/hooks/usePlpSidQueryHandlers';
import { usePlpPincode } from '@/features/plp/presentation/hooks/usePlpPincode';
import { usePlpPresentation } from '@/features/plp/presentation/hooks/usePlpPresentation';
import {
  usePlpAnalytics,
  type PlpAnalyticsPageType,
} from '@/features/plp/presentation/hooks/usePlpAnalytics';
import { useIsDesktop, useIsMobile } from '@/core/hooks/useMediaQuery';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import type { ListingMeta, ListingPageResult } from '@/features/plp/domain/models/ProductListing';
import type { ListingMetaPresentationVm } from '@/features/plp/presentation/viewModels/plp.vm';
import type { PlpSortByValue } from '@/features/plp/constants';
import { getSidFromSearch } from '@/pages/product-listing/searchPlpUtils';
import { applySearchPlpMetaPresentation } from '@/pages/product-listing/searchPlpListingMeta';
import { facebookSearchEvent } from '@/features/search/presentation/analytics/facebookSearchEvent';
import { consumeSearchTriggered } from '@/features/search/utils/searchSession';

export type MapSearchPlpListingMeta = (
  listingMeta: ListingMeta,
  metaPresentation: ListingMetaPresentationVm | null,
) => ListingMeta;

export interface UseSearchPlpShellParams {
  pathname: string;
  listingKey: string;
  fetchPage: (pageNumber: number, opts?: { signal?: AbortSignal }) => Promise<ListingPageResult>;
  tags: string;
  pageType: PlpAnalyticsPageType;
  sortBy: PlpSortByValue;
  mapListingMeta?: MapSearchPlpListingMeta;
}

export function useSearchPlpShell({
  pathname,
  listingKey,
  fetchPage,
  tags,
  pageType,
  sortBy,
  mapListingMeta = applySearchPlpMetaPresentation,
}: UseSearchPlpShellParams) {
  const location = useLocation();
  const sidFromUrl = useMemo(() => getSidFromSearch(location.search), [location.search]);

  const { ref: topSentinelRef, isIntersecting: isTopVisible } = useIntersectionObserver<HTMLDivElement>();
  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();
  const footerInViewport = useAppSelector(selectFooterInViewport);
  const shouldPauseInfiniteScroll = useCallback(() => footerInViewport, [footerInViewport]);

  const [isNoStoreFound, setIsNoStoreFound] = useState(false);
  const [pincodeDescription, setPincodeDescription] = useState<string | undefined>(undefined);

  const {
    pincode,
    sid,
    inputValue,
    error: pincodeError,
    isPopupOpen,
    openPopup,
    closePopup,
    handleChange,
    handleSubmit,
  } = usePlpPincode();

  useEffect(() => {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    return () => {
      if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
    };
  }, []);

  const plp = usePlpListing({
    fetchPage,
    pathname,
    initialData: null,
    listingKey,
    restoreKey: listingKey,
    isMobile,
    shouldPauseInfiniteScroll,
  });

  const plpAnalytics = usePlpAnalytics({
    pageType,
    listingHeader: plp.listingMeta?.header ?? '',
    collectionTags: tags,
    currentSortBy: sortBy,
    totalCount: plp.listingMeta?.totalCount ?? 0,
  });

  const pageViewFiredRef = useRef<string | null>(null);
  const fbSearchFiredRef = useRef<string | null>(null);
  useEffect(() => {
    plpAnalytics.viewedProductIds.clear();
  }, [listingKey, plpAnalytics.viewedProductIds]);

  useEffect(() => {
    fbSearchFiredRef.current = null;
  }, [listingKey]);

  useEffect(() => {
    if (!plp.listingMeta) return;
    if (fbSearchFiredRef.current === listingKey) return;
    
    const isImageSearch = typeof window !== 'undefined' && window.location.pathname.includes('/search/image');
    if (!isImageSearch && !consumeSearchTriggered()) return;
 
    fbSearchFiredRef.current = listingKey;
    facebookSearchEvent({
      designs: plp.allDesigns.map((design) => ({
        designId: design.designId,
      })),
      result_count: plp.listingMeta.totalCount ?? 0,
      sort_order: sortBy,
      surface_title: plp.listingMeta.header,
    });
  }, [plp.listingMeta, plp.allDesigns, listingKey, sortBy]);

  useEffect(() => {
    if (plp.isLoading) return;
    if (!plp.listingMeta) return;
    if (plp.allDesigns.length === 0) return;
    if (!plpAnalytics.isReady) return;
    if (pageViewFiredRef.current === listingKey) return;
    pageViewFiredRef.current = listingKey;
    plpAnalytics.trackPageView(plp.allDesigns);
  }, [plp.isLoading, plp.listingMeta, plp.allDesigns, listingKey, plpAnalytics]);

  useEffect(() => {
    const handleScroll = () => plpAnalytics.trackScrollDown();
    window.addEventListener('scroll', handleScroll, { passive: true, once: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [plpAnalytics]);

  const { handleActivateStoreDesigns, handleShowAllDesigns } = usePlpSidQueryHandlers({
    sidFromUrl,
    sid,
    pincode,
    onOpenPincodePopup: openPopup,
    onNoStoreFound: () => setIsNoStoreFound(true),
  });

  const handleOpenBottomPincode = useCallback(() => {
    plpAnalytics.trackPincodeClick(pincode ?? '');
    setPincodeDescription('Browse our exquisite designs: locate stores near you!');
    openPopup();
  }, [openPopup, plpAnalytics, pincode]);

  const handlePincodeSubmit = useCallback(async () => {
    plpAnalytics.trackPincodeEntered(inputValue, !/^[1-9][0-9]{5}$/.test(inputValue.trim()));
    await handleSubmit();
  }, [plpAnalytics, inputValue, handleSubmit]);

  const handlePincodeOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        openPopup();
      } else {
        setPincodeDescription(undefined);
        closePopup();
      }
    },
    [openPopup, closePopup],
  );

  const { metaPresentation, breadcrumbItems, storeCountLabel } = usePlpPresentation({
    listingMeta: plp.listingMeta,
    pathname,
    isStorePlp: false,
    isSearchPlp: true,
  });

  const displayListingMeta = useMemo(() => {
    if (!plp.listingMeta) return null;
    return mapListingMeta(plp.listingMeta, metaPresentation);
  }, [plp.listingMeta, metaPresentation, mapListingMeta]);

  usePlpRedirectUrlNavigation({ redirectUrl: plp.redirectUrl, sidFromUrl });

  const showScrollToTop = isDesktop && !isTopVisible;

  return {
    topSentinelRef,
    showScrollToTop,
    isDesktop,
    plp,
    plpAnalytics,
    displayListingMeta,
    breadcrumbItems,
    storeCountLabel,
    sidFromUrl,
    isNoStoreFound,
    pincode,
    handleOpenBottomPincode,
    handleActivateStoreDesigns,
    handleShowAllDesigns,
    pincodeProps: {
      open: isPopupOpen,
      onOpenChange: handlePincodeOpenChange,
      inputValue,
      error: pincodeError,
      description: pincodeDescription,
      onChange: handleChange,
      onSubmit: handlePincodeSubmit,
    },
  };
}

export type SearchPlpShell = ReturnType<typeof useSearchPlpShell>;
